/**
 * UpdateBannerAgent
 * 
 * Agent responsible for managing the update notification UI.
 * 
 * Skills:
 * - SHOW_BANNER: Display update notification
 * - HIDE_BANNER: Dismiss notification
 * - TRIGGER_RELOAD: Force page refresh
 * - MOBILE_TOAST: Mobile-optimized notification
 */

import VersionCheckAgent from './VersionCheckAgent';

const UpdateBannerAgent = {
  name: 'UpdateBannerAgent',
  
  // Internal state
  _bannerVisible: false,
  _stateCallbacks: [],
  
  skills: {
    /**
     * SHOW_BANNER
     * Signals that the banner should be displayed
     * @param {object} options - Banner options
     * @param {string} options.message - Message to display
     * @param {string} options.type - 'info' | 'warning'
     * @returns {void}
     */
    SHOW_BANNER: (options = {}) => {
      const message = options.message || 'A new version is available!';
      const type = options.type || 'info';
      
      UpdateBannerAgent._bannerVisible = true;
      UpdateBannerAgent._notifyStateChange({
        visible: true,
        message,
        type
      });
    },
    
    /**
     * HIDE_BANNER
     * Dismisses the banner
     * @returns {void}
     */
    HIDE_BANNER: () => {
      UpdateBannerAgent._bannerVisible = false;
      UpdateBannerAgent._notifyStateChange({
        visible: false,
        message: '',
        type: 'info'
      });
    },
    
    /**
     * TRIGGER_RELOAD
     * Reloads the page to get the new version
     * @param {object} options - Reload options
     * @param {boolean} options.hard - Force hard reload (bypass cache)
     * @returns {void}
     */
    TRIGGER_RELOAD: (options = {}) => {
      const hard = options.hard !== false; // Default to hard reload
      
      // Update stored version before reload to prevent loop
      const newVersion = options.newVersion;
      if (newVersion) {
        VersionCheckAgent.skills.STORE_VERSION(newVersion);
      }
      
      if (hard) {
        // Hard reload - bypasses cache
        window.location.reload(true);
      } else {
        // Soft reload
        window.location.reload();
      }
    },
    
    /**
     * MOBILE_TOAST
     * Shows a mobile-optimized toast notification
     * @param {object} options - Toast options
     * @param {string} options.message - Message to display
     * @param {number} options.duration - Duration in ms (0 = persistent)
     * @returns {void}
     */
    MOBILE_TOAST: (options = {}) => {
      const message = options.message || 'Update available';
      const duration = options.duration || 0; // Persistent by default
      
      // For mobile, we use the same banner but with toast styling
      UpdateBannerAgent.skills.SHOW_BANNER({
        message,
        type: 'toast'
      });
      
      // Auto-hide after duration if specified
      if (duration > 0) {
        setTimeout(() => {
          UpdateBannerAgent.skills.HIDE_BANNER();
        }, duration);
      }
    },
    
    /**
     * SUBSCRIBE
     * Subscribe to banner state changes
     * @param {function} callback - Callback for state changes
     * @returns {function} Unsubscribe function
     */
    SUBSCRIBE: (callback) => {
      UpdateBannerAgent._stateCallbacks.push(callback);
      
      // Return unsubscribe function
      return () => {
        const index = UpdateBannerAgent._stateCallbacks.indexOf(callback);
        if (index > -1) {
          UpdateBannerAgent._stateCallbacks.splice(index, 1);
        }
      };
    },
    
    /**
     * GET_STATE
     * Gets current banner state
     * @returns {{visible: boolean, message: string, type: string}}
     */
    GET_STATE: () => {
      return {
        visible: UpdateBannerAgent._bannerVisible,
        message: '',
        type: 'info'
      };
    }
  },
  
  /**
   * Notify all subscribers of state change
   * @private
   */
  _notifyStateChange: (state) => {
    UpdateBannerAgent._stateCallbacks.forEach(cb => {
      try {
        cb(state);
      } catch (error) {
        console.warn('[UpdateBannerAgent] Callback error:', error);
      }
    });
  },
  
  /**
   * Execute a skill by name
   */
  execute: async (skillName, ...args) => {
    const skill = UpdateBannerAgent.skills[skillName];
    if (!skill) {
      throw new Error(`[UpdateBannerAgent] Unknown skill: ${skillName}`);
    }
    return await skill(...args);
  }
};

export default UpdateBannerAgent;
